// Check a video before it goes live: upload the creator's file or paste a
// Drive / YouTube / Instagram link. QC starts as soon as it's added.
import React, { useEffect, useRef, useState } from 'react';
import { errorText } from './qcClient';
import { Button, C, Modal } from './ui';

const LINK_HOSTS = /^https:\/\/([a-z0-9-]+\.)*(drive\.google\.com|docs\.google\.com|youtube\.com|youtu\.be|instagram\.com)\//i;
const MAX_MB = 1024;

export const checkLink = (value) => {
    const v = value.trim();
    if (!v) return 'Paste a link';
    if (!/^https:\/\//i.test(v)) return 'Use an https:// link';
    if (!LINK_HOSTS.test(v)) return 'Use a Google Drive, YouTube or Instagram link, or upload the file instead';
    return null;
};

const tabStyle = (active) => ({
    flex: 1, minHeight: 34, padding: '6px 0', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer', borderRadius: 8,
    background: active ? '#fff' : 'transparent', color: active ? C.text : C.muted, border: 'none',
    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
});

const inputStyle = { width: '100%', padding: '9px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };

// Hidden from view but still in the tab order, so the drop zone works from the keyboard.
const visuallyHidden = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

export default function AddDraftModal({ client, onClose, onAdded }) {
    const [mode, setMode] = useState('upload');
    const [title, setTitle] = useState('');
    const [link, setLink] = useState('');
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    // An upload that worked is kept, so a retry after a failed "create" step
    // doesn't send the whole file again. It's dropped if the file changes.
    const [uploaded, setUploaded] = useState(null);
    const abort = useRef(null);

    useEffect(() => () => abort.current?.abort(), []);

    const fileError = file && file.size > MAX_MB * 1048576 ? `File is over ${MAX_MB} MB` : null;
    const linkError = mode === 'link' && link ? checkLink(link) : null;
    const ready = mode === 'upload' ? Boolean(file) && !fileError : link.trim() && !checkLink(link);

    const getSource = async () => {
        if (mode === 'link') return link.trim();
        if (uploaded?.file === file) return uploaded.source;
        abort.current = new AbortController();
        const source = await client.upload(file, { signal: abort.current.signal });
        setUploaded({ file, source });
        return source;
    };

    const submit = async () => {
        setSaving(true);
        setError('');
        try {
            const source = await getSource();
            const name = title.trim() || (mode === 'upload' ? file.name : 'Draft video');
            const video = await client.createVideo({ source, title: name, external_ref: null });
            onAdded(video, mode);
        } catch (err) {
            if (err?.name !== 'AbortError') setError(errorText(err));
            setSaving(false);
        }
    };

    // Cancel always works, including mid-upload.
    const cancel = () => {
        abort.current?.abort();
        onClose();
    };

    return (
        <Modal title="Check a draft video" subtitle="QC starts as soon as the video is added" onClose={cancel} width={480}
            footer={(
                <>
                    <Button onClick={cancel}>Cancel</Button>
                    <Button variant="primary" onClick={submit} disabled={!ready || saving}>{saving ? (mode === 'upload' ? 'Uploading…' : 'Adding…') : 'Add & run QC'}</Button>
                </>
            )}>
            <div style={{ display: 'flex', gap: 4, background: C.line, padding: 4, borderRadius: 10, marginBottom: 16 }}>
                <button type="button" className="vqc-focus" aria-pressed={mode === 'upload'} style={tabStyle(mode === 'upload')} onClick={() => setMode('upload')}>Upload file</button>
                <button type="button" className="vqc-focus" aria-pressed={mode === 'link'} style={tabStyle(mode === 'link')} onClick={() => setMode('link')}>Paste link</button>
            </div>

            {mode === 'upload' ? (
                <label className="vqc-drop" style={{ position: 'relative', display: 'block', border: `2px dashed ${C.border}`, borderRadius: 12, padding: 24, textAlign: 'center', cursor: 'pointer', background: C.soft }}>
                    <input type="file" accept="video/*" aria-label="Video file" style={visuallyHidden}
                        onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    <div style={{ fontSize: 28, marginBottom: 6 }}>📤</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{file ? file.name : 'Choose a video file'}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                        {file ? `${(file.size / 1048576).toFixed(1)} MB` : `MP4 or MOV, up to ${MAX_MB} MB`}
                    </div>
                </label>
            ) : (
                <div>
                    <input aria-label="Video link" value={link} onChange={(e) => setLink(e.target.value)} style={inputStyle}
                        placeholder="https://drive.google.com/file/d/… or a YouTube / Instagram link" />
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Drive files must be shared as "Anyone with the link". Instagram links may fail; uploading is more reliable.</div>
                </div>
            )}

            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.body, marginTop: 16 }}>
                Title <span style={{ fontWeight: 400, color: C.muted }}>(optional)</span>
                <input aria-label="Title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Creator name - reel v2"
                    style={{ ...inputStyle, marginTop: 6 }} />
            </label>

            {(error || fileError || linkError) && (
                <div role="alert" style={{ marginTop: 12, fontSize: 12, color: '#991b1b', background: '#fee2e2', borderRadius: 8, padding: '8px 12px' }}>
                    {error || fileError || linkError}
                </div>
            )}
        </Modal>
    );
}
